/**
 * Thin client for ants-registration.worker.ts, matching the runWorker()
 * pattern used elsewhere in this app (see ttp-client.ts). Both the worker
 * and the Emscripten module it loads (real `antsRegistration`/
 * `antsApplyTransforms`, compiled from ANTsX/ANTs C++ source) are real —
 * validated NCC=0.9984 vs native ANTs on real data, see
 * `openads-wasm/app/src/main.ts`'s registration stage for the citation
 * trail. (An earlier version of this comment called the module a stub;
 * that was stale documentation left over from before the build finished,
 * not current fact — see `workers/ANTS_INTEGRATION.md` for the real history.)
 */
import { runWorker, ab } from "./worker-client";
import type { Dims } from "./volume";

import { appPath } from "./assets";
export type AntsRegistrationStep = "affine" | "syn" | "moco";

/** Registers against an arbitrary volume instead of the worker's bundled MNI
 *  template — subject-to-subject coregistration (e.g. PWI's own native mask
 *  to DWI's native mask, registration_pwi_align.py Step 1), not subject-to-atlas. */
export interface AntsFixedOverride {
  voxels: Float32Array;
  dims: Dims;
  pixdim: Dims;
  affine: Float32Array;
}

export interface AntsRegistrationInput {
  step: AntsRegistrationStep;
  voxels: Float32Array; // F-order, moving volume
  dims: Dims;
  pixdim: Dims;
  affine: Float32Array; // 4x4 row-major voxel->world
  fixedOverride?: AntsFixedOverride;
}

export interface AntsRegistrationResult {
  step: AntsRegistrationStep;
  registered: Float32Array;
  dims: Dims;
  pixdim: Dims;
  affine: Float32Array;
  /** Opaque transform file bytes keyed by their antsRegistration output filename (.mat / warp .nii.gz). */
  transformFiles: Record<string, ArrayBuffer>;
  totalMs: number;
  log: string[];
}

/** Runs one antsRegistration stage. To chain affine -> syn (the real pipeline's shape), call this
 *  twice: feed the first call's `registered`/`dims`/`pixdim`/`affine` back in as the second call's input. */
export function runAntsRegistration(
  input: AntsRegistrationInput,
  onProgress?: (stage: string) => void
): Promise<AntsRegistrationResult> {
  const worker = new Worker(appPath("workers/ants-registration.worker.js"), { type: "module" });
  const payload = {
    step: input.step,
    voxels: ab(input.voxels),
    dims: input.dims,
    pixdim: input.pixdim,
    affine: ab(input.affine),
    fixedOverride: input.fixedOverride
      ? {
          voxels: ab(input.fixedOverride.voxels),
          dims: input.fixedOverride.dims,
          pixdim: input.fixedOverride.pixdim,
          affine: ab(input.fixedOverride.affine),
        }
      : undefined,
  };
  type WireResult = Omit<AntsRegistrationResult, "registered" | "affine"> & {
    registered: ArrayBuffer;
    affine: ArrayBuffer;
  };
  return runWorker<typeof payload, WireResult>(worker, payload, onProgress)
    .then((d) => ({
      ...d,
      registered: new Float32Array(d.registered),
      affine: new Float32Array(d.affine),
    }))
    .finally(() => worker.terminate());
}

/** One `-t` entry for antsApplyTransforms, first = closest to output/fixed space. */
export interface AntsTransformFile {
  /** FS path to stage this transform at, e.g. "/t0.mat" or "/t0.nii.gz" — extension matters, ITK picks gzip-vs-not from it. */
  path: string;
  bytes: ArrayBuffer;
  /** Apply this transform's inverse (antsApplyTransforms `-t [path,1]`, the
   *  CLI equivalent of ANTsPy's `whichtoinvert=[True]`) — reuses an
   *  already-computed forward transform instead of a separately stored one. */
  invert?: boolean;
}

export interface AntsApplyTransformInput {
  voxels: Float32Array;
  dims: Dims;
  pixdim: Dims;
  affine: Float32Array;
  transforms: AntsTransformFile[];
  /** Which of the worker's two bundled MNI templates defines the output grid.
   *  Ignored (but still required by the wire type) when `fixedOverride` is set. */
  fixedTemplate: "affine" | "syn";
  /** Real pipeline: nearestNeighbor for mask/stroke channels, linear for intensity channels. */
  interpolator?: "linear" | "nearestNeighbor";
  /** Output grid is this volume instead of one of the two MNI templates —
   *  e.g. DWI's own native mask, for landing PWI's TTP into "DWI-native"
   *  space per registration_pwi_align.py Step 1's apply-transforms call. */
  fixedOverride?: AntsFixedOverride;
}

export interface AntsApplyTransformResult {
  registered: Float32Array;
  dims: Dims;
  pixdim: Dims;
  affine: Float32Array;
  totalMs: number;
  log: string[];
}

/** Pushes a channel through an already-computed antsRegistration transform
 *  (or ordered chain of transforms) — `ants.apply_transforms(...)`'s real
 *  WASM equivalent. Validated in isolation vs native ANTsPy on the same
 *  transform files: NCC=1.0, max abs diff ~2e-10 (float noise). */
export function runAntsApplyTransform(
  input: AntsApplyTransformInput,
  onProgress?: (stage: string) => void,
): Promise<AntsApplyTransformResult> {
  const worker = new Worker(appPath("workers/ants-registration.worker.js"), { type: "module" });
  const payload = {
    step: "apply-transform" as const,
    voxels: ab(input.voxels),
    dims: input.dims,
    pixdim: input.pixdim,
    affine: ab(input.affine),
    transforms: input.transforms,
    fixedTemplate: input.fixedTemplate,
    interpolator: input.interpolator,
    fixedOverride: input.fixedOverride
      ? {
          voxels: ab(input.fixedOverride.voxels),
          dims: input.fixedOverride.dims,
          pixdim: input.fixedOverride.pixdim,
          affine: ab(input.fixedOverride.affine),
        }
      : undefined,
  };
  type WireResult = Omit<AntsApplyTransformResult, "registered" | "affine"> & {
    registered: ArrayBuffer;
    affine: ArrayBuffer;
  };
  return runWorker<typeof payload, WireResult>(worker, payload, onProgress)
    .then((d) => ({
      ...d,
      registered: new Float32Array(d.registered),
      affine: new Float32Array(d.affine),
    }))
    .finally(() => worker.terminate());
}
